import React, { useEffect, useState } from 'react';
import { galleryService } from '../services';
import { resolveMediaUrl } from '../services/api';
import PageBreadcrumb from '../components/PageBreadcrumb';

const Gallery = () => {
    const [images, setImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchGalleryData();
    }, []);

    const fetchGalleryData = async () => {
        try {
            const [imagesRes, categoriesRes] = await Promise.all([
                galleryService.getAll(),
                galleryService.getCategories(),
            ]);
            setImages(imagesRes.data.data || []);
            setCategories(categoriesRes.data || []);
        } catch (error) {
            console.error('Error fetching gallery:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredImages = selectedCategory
        ? images.filter((img) => String(img.gallery_category_id) === String(selectedCategory))
        : images;

    return (
        <div className="bg-gray-50">
            <PageBreadcrumb
                title="Gallery"
                description="Capturing our impact in photos"
                items={[{ label: 'Gallery' }]}
            />

            <section className="py-16">
                <div className="mx-auto max-w-7xl px-4">
                    <div className="mb-8 flex flex-wrap gap-3">
                        <button
                            onClick={() => setSelectedCategory(null)}
                            className={`rounded-full px-4 py-2 font-semibold transition ${selectedCategory === null ? 'bg-emerald-600 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
                        >
                            All
                        </button>
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`rounded-full px-4 py-2 font-semibold transition ${String(selectedCategory) === String(category.id) ? 'bg-emerald-600 text-white' : 'bg-white text-gray-800 hover:bg-gray-100'}`}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="py-12 text-center">
                            <div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {filteredImages.map((image) => (
                                <div key={image.id} className="group relative overflow-hidden rounded-lg shadow-lg transition hover:shadow-xl">
                                    <img src={resolveMediaUrl(image.image_path)} alt={image.alt_text} className="h-64 w-full object-cover transition group-hover:scale-110" />
                                    <div className="absolute inset-0 flex items-center justify-center bg-black opacity-0 transition group-hover:opacity-40">
                                        <button
                                            onClick={() => setSelectedImage(image)}
                                            className="rounded-lg bg-emerald-600 px-6 py-2 text-white opacity-0 transition hover:bg-emerald-700 group-hover:opacity-100"
                                        >
                                            View
                                        </button>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 text-white">
                                        <p className="text-sm font-semibold">{image.alt_text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {selectedImage && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setSelectedImage(null)}>
                    <div className="max-h-[90vh] max-w-4xl overflow-hidden rounded-lg bg-white" onClick={(e) => e.stopPropagation()}>
                        <img src={resolveMediaUrl(selectedImage.image_path)} alt={selectedImage.alt_text} className="max-h-[80vh] w-full object-contain" />
                        <div className="flex items-center justify-between p-4">
                            <div>
                                <p className="font-semibold text-gray-800">{selectedImage.alt_text}</p>
                                <p className="text-sm text-gray-500">{selectedImage.gallery_category?.name || selectedImage.category}</p>
                            </div>
                            <button onClick={() => setSelectedImage(null)} className="rounded bg-gray-200 px-3 py-1 text-sm text-gray-700 hover:bg-gray-300">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;
